import React from 'react';
import { useDraggable } from '@dnd-kit/core';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import { PALETTE_MODULES, MODULE_STYLES } from './constants';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import TableBarIcon from '@mui/icons-material/TableBar';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import LocalBarIcon from '@mui/icons-material/LocalBar';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';

// Az ikonok mérete a `size` prop alapján skálázódik (compact módban), hogy telefon
// szélességű toolboxba is beférjen görgetés nélkül.
export const ICON_MAP = {
  round_table:  (fontSize) => <TableBarIcon sx={{ fontSize }} />,
  rect_table:   (fontSize) => <TableRestaurantIcon sx={{ fontSize }} />,
  stage:        (fontSize) => <MusicNoteIcon sx={{ fontSize }} />,
  dancefloor:   (fontSize) => <span style={{ fontSize: fontSize - 2 }}>💃</span>,
  photowall:    (fontSize) => <PhotoCameraIcon sx={{ fontSize }} />,
  bar:          (fontSize) => <LocalBarIcon sx={{ fontSize }} />,
};

/**
 * Draggable module palette.
 * Drag items onto canvas, or click to place at canvas center.
 *
 * Props:
 *   onItemClick – (moduleType, isTable) => void  (click-to-center)
 *   orientation – 'column' (default, label melletti sor) | 'row' (kompakt, csak ikon,
 *                 egymás mellett — toolbox nézethez, hogy görgetés nélkül elférjen)
 *   size        – 'row' módban a négyzet ikon mérete px-ben (alapértelmezett 44, telefonon
 *                 kisebbre állítva, hogy az egész sor elférjen görgetés nélkül)
 */
export default function ModulePalette({ onItemClick, orientation = 'column', size = 44 }) {
  const isRow = orientation === 'row';
  return (
    <Box sx={{ display: 'flex', flexDirection: isRow ? 'row' : 'column', flexWrap: isRow ? 'nowrap' : undefined, gap: isRow ? 0.5 : 0.5 }}>
      {PALETTE_MODULES.map(module => (
        <PaletteItem key={module.type} module={module} onItemClick={onItemClick} compact={isRow} size={size} />
      ))}
    </Box>
  );
}

function PaletteItem({ module, onItemClick, compact = false, size = 44 }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `palette-${module.type}`,
    // `compact`+`size` úgy adja át a húzott elem méretét/nézetét, hogy a DragOverlay
    // pontosan ugyanakkora ikon-doboz legyen, mint az eredeti — így a kurzor pontosan
    // a húzott elem közepén marad, nem tolódik el mellé.
    data: { type: 'PALETTE_ITEM', moduleType: module.type, isTable: module.isTable, compact, size },
  });

  const style_ = MODULE_STYLES[module.type] || { bgcolor: '#e0e0e0', color: '#616161' };
  const iconFontSize = Math.round(size * 0.45);

  const paper = (
    <Paper
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      elevation={isDragging ? 4 : 1}
      onClick={() => onItemClick?.(module.type, module.isTable)}
      sx={compact ? {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: size,
        height: size,
        flexShrink: 0,
        cursor: 'grab',
        // A kompakt sor vízszintesen görgethető (overflowX:auto) — touch-action nélkül a
        // natív görgetés elviszi az érintést a dnd-kit pointer-drag elől mobilon.
        touchAction: 'none',
        opacity: isDragging ? 0.6 : 1,
        bgcolor: isDragging ? style_.bgcolor + 'cc' : '#fff',
        border: '1px solid',
        borderColor: isDragging ? '#2a5d32' : 'divider',
        borderRadius: 1.5,
        userSelect: 'none',
        transition: 'all 0.12s',
        '&:hover': {
          bgcolor: style_.bgcolor + '66',
          borderColor: style_.color,
        },
      } : {
        display: 'flex',
        alignItems: 'center',
        gap: 1,
        p: '6px 10px',
        cursor: 'grab',
        touchAction: 'none',
        opacity: isDragging ? 0.6 : 1,
        bgcolor: isDragging ? style_.bgcolor + 'cc' : '#fff',
        border: '1px solid',
        borderColor: isDragging ? '#2a5d32' : 'transparent',
        borderRadius: 1.5,
        userSelect: 'none',
        transition: 'all 0.12s',
        '&:hover': {
          bgcolor: style_.bgcolor + '66',
          borderColor: style_.color,
        },
      }}
    >
      <Box sx={{ color: style_.color, display: 'flex', alignItems: 'center' }}>
        {ICON_MAP[module.type]
          ? ICON_MAP[module.type](compact ? iconFontSize : 20)
          : <span>{module.type[0].toUpperCase()}</span>}
      </Box>
      {!compact && (
        <Typography variant="caption" sx={{ fontSize: 11, fontWeight: 500 }}>
          {module.label}
        </Typography>
      )}
    </Paper>
  );

  return compact ? <Tooltip title={module.label}>{paper}</Tooltip> : paper;
}
